import { Column } from "components/Layout/Grid";
import ManagementPanelModalCard from "components/Modal/ManagementPanelModalCard";
import Table from "components/Table";
import { statusProcessing } from "components/TablePortal/interface";
import toast from "components/Toast/dispatch";
import Parse from "html-react-parser";
import ErrorIcon from "img/error-img.svg";
import { toastNonstandardCode } from "pages/Quotes/Dashboard/utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ManagementPanelService from "services/managementPanel.service";
import QuoteServices from "services/quote.service";
import { RootState } from "store/ducks";
import type { ILostItemRow } from "../ModalLostItems/LostItemsDetailDropdown";
import {
  WonItemsDetailDropdown,
  type IWonItemDetailSubItem,
  type IWonItemRow,
} from "./WonItemsDetailDropdown";
import { wonItemsTableModel } from "./wonItemsTableModel";

const PAGE_SIZE = 10;

const MOCK_WON_ITEMS_RESPONSE = {
  data: [] as IWonItemRow[],
  total: 0,
};

function mapApiItemToWonItemRow(item: any): IWonItemRow {
  const code =
    item.productCode ??
    item.product?.code;
  const description =
    item.productDescription ??
    item.product?.description;
  const brand =
    item.manufacturer ??
    item.marca ??
    item.product?.brand ??
    item.preferedBrands;

  const rawSubItems = item.subItems ?? item.quotations ?? item.cotacoes ?? item.items ?? item.itens ?? [];
  const subItems: IWonItemDetailSubItem[] = Array.isArray(rawSubItems)
    ? rawSubItems.map((sub: any) => ({
        quotationId:
          sub.quotationId ??
          sub.quotation_id ??
          sub.cotacaoId ??
          sub.cotacao_id ??
          sub.id,
        quotationCode:
          sub.codeQuotation ??
          sub.quotationCode,
        codeQuotation:
          sub.codeQuotation ??
          sub.quotationCode ??
          sub.codigoCotacao ??
          sub.codigo,
        createdAt:
          sub.quotationCreatedAt ??
          sub.createdAt ??
          sub.creationDate,
        customerCode:
          sub.customerCode ??
          sub.crmCustomerCode ??
          sub.buyer?.identifier ??
          sub.company?.crmCustomerCode,
        razaoSocial:
          sub.buyerName ??
          sub.buyer?.razaoSocial,
        category:
          sub.categoryMafra ??
          sub.quotationCategoryMafraDescription,
        sentPrice:
          sub.salePrice ??
          sub.price,
        origin: sub.quotationOrigin ?? sub.origem,
        quotationOrigin: sub.quotationOrigin ?? sub.origem,
        status: sub.status,
        supplierStatus: sub.supplierStatus ?? sub.supplierStatusDescription,
      }))
    : [];

  return {
    code,
    description,
    brand,
    subItems,
  };
}

function wonItemQuotationOriginLabel(row: ILostItemRow): string {
  const o = row.quotationOrigin;
  if (o === 0 || o === "Bionexo") return "Bionexo";
  return "MVM";
}

export interface ModalWonItemsProps {
  open: boolean;
  onClose: () => void;
  filterCurrent: Record<string, any>;
}

export const ModalWonItems: React.FC<ModalWonItemsProps> = ({
  open,
  onClose,
  filterCurrent,
}) => {
  const user = useSelector((state: RootState) => state.user);
  const quoteItemStorage = useSelector((state: RootState) => state.quoteItem) ?? { id: "" };
  const blockedSimultaneousAccess = process.env.REACT_APP_BLOCKED_SIMULTANEOUS_ACCESS;
  const bcgOnly = process.env.REACT_APP_BCG_ONLY === "true";

  const _managementPanelService = new ManagementPanelService();
  const _quotesServices = new QuoteServices();

  const searchValueRef = useRef("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  const [wonItemsTableData, setWonItemsTableData] = useState<IWonItemRow[]>([]);
  const [totalWonItemsCount, setTotalWonItemsCount] = useState(0);
  const [loadingWonItemsTable, setLoadingWonItemsTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [tableOrder, setTableOrder] = useState("");

  const totalPages = Math.ceil(totalWonItemsCount / PAGE_SIZE) || 1;
  const wonItemsModel = useMemo(() => wonItemsTableModel(), []);

  const handleOpenQuotation = useCallback(
    async (row: ILostItemRow) => {
      const id = row.quotationId;
      if (!id) {
        toast.info(
          "Cotação indisponível",
          "Não foi possível identificar a cotação para abrir o detalhe."
        );
        return;
      }

      const code = row.codeQuotation ?? "";
      if (code.length > 9) {
        toastNonstandardCode(wonItemQuotationOriginLabel(row));
        return;
      }

      if (!row.customerCode) {
        toast.info(
          "Cliente não cadastrado",
          "Para continuar com a cotação, é necessário cadastrar o cliente antes."
        );
        return;
      }

      const isDisconsidered = row.supplierStatus === "DISREGARDED";
      if (isDisconsidered && (bcgOnly || user?.isBCGUser)) {
        toast.info("Cotação desconsiderada", "A cotação foi desconsiderada por condições comerciais.");
        return;
      }

      if (statusProcessing.includes(row.status ?? "")) {
        return;
      }

      const openInNewTab = (path: string) => {
        window.open(path, "_blank", "noopener,noreferrer");
      };

      try {
        const response = await _quotesServices.checkQuotationViewed(id);
        const users: string[] = response?.data?.data?.user ?? [];

        if (
          blockedSimultaneousAccess &&
          users?.length > 0 &&
          !users.includes(user?.fullName ?? "") &&
          quoteItemStorage
        ) {
          const description = Parse(
            `<p class='text-styles color-blue-gray mt-3 text-wrap text-center description-quote-blocked'>Essa cotação está sendo manipulada pelo colaborador <b>${users[0]}</b>. Aguarde a finalização para acessar as informações.</p>`
          );
          toast.errorCustom(
            "Importante!",
            description,
            ErrorIcon,
            true,
            "Fechar",
            19,
            14,
            true
          );
          return;
        }

        openInNewTab(`/gravado-no-money/${id}`);
      } catch {
        openInNewTab(`/cotacoes-nova/${id}`);
      }
    },
    [user, quoteItemStorage, blockedSimultaneousAccess, bcgOnly]
  );

  async function loadWonItems(page: number = 0, userEmail: string = selectedUserEmail) {
    setLoadingWonItemsTable(true);
    try {
      const searchTerm = searchValueRef.current.trim();
      const trackedUser = userEmail.trim();
      const filterMapping = {
        ProductCategories: filterCurrent.ProductCategories,
        SellerCodes: filterCurrent.SellerCodes,
        Origin: filterCurrent.Origin,
        BusinessUnitIdentifier: filterCurrent.BusinessUnitIdentifier,
        StartDate: filterCurrent.StartDate,
        EndDate: filterCurrent.EndDate,
        Top: PAGE_SIZE,
        Skip: page * PAGE_SIZE,
        ...(searchTerm
          ? {
              Manufacturer: searchTerm,
              CompanyCrmCustomerCode: searchTerm,
              BuyerName: searchTerm,
              Code: searchTerm,
              Description: searchTerm,
            }
          : {}),
        ...(trackedUser ? { TrackedUser: trackedUser } : {}),
      };

      let response: any;
      try {
        response = await _managementPanelService.getDataInsightsRAItemsDetailsByStatus(
          2,
          filterMapping
        );
      } catch {
        response = MOCK_WON_ITEMS_RESPONSE;
      }

      const rawData = response?.data ?? response ?? [];
      const total = response?.total ?? (Array.isArray(rawData) ? rawData.length : 0);
      const rows: IWonItemRow[] = Array.isArray(rawData)
        ? rawData.map((item: any) => mapApiItemToWonItemRow(item))
        : [];

      const dataToUse = rows.length > 0 ? rows : MOCK_WON_ITEMS_RESPONSE.data.map((item: any) => mapApiItemToWonItemRow(item));
      const totalToUse = rows.length > 0 ? (typeof total === "number" ? total : rows.length) : MOCK_WON_ITEMS_RESPONSE.total;

      setWonItemsTableData(dataToUse);
      setTotalWonItemsCount(totalToUse);
      setCurrentPage(page);
    } catch {
      const fallbackRows = MOCK_WON_ITEMS_RESPONSE.data.map((item: any) => mapApiItemToWonItemRow(item));
      setWonItemsTableData(fallbackRows);
      setTotalWonItemsCount(MOCK_WON_ITEMS_RESPONSE.total);
    } finally {
      setLoadingWonItemsTable(false);
    }
  }

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  function handleKeyDown(key: string) {
    if (key === "Enter") {
      setCurrentPage(0);
      loadWonItems(0);
    }
  }

  function handlePageChange(newPage: number) {
    setCurrentPage(newPage);
    loadWonItems(newPage);
  }

  if (!open) return null;

  return (
    <ManagementPanelModalCard
      title="Itens ganhos"
      searchFieldLabel="Buscar código do item, descrição ou marca"
      isLoading={loadingWonItemsTable}
      onSearchSubmit={(value) => {
        searchValueRef.current = value;
      }}
      onCloseAction={onClose}
      onKeyDown={handleKeyDown}
      onOpen={() => {
        searchValueRef.current = "";
        setSelectedUserEmail("");
        setCurrentPage(0);
        loadWonItems(0, "");
      }}
      onUserSelect={(selectedUser) => {
        setSelectedUserEmail(selectedUser.email);
        setCurrentPage(0);
        loadWonItems(0, selectedUser.email);
      }}
      onUserClear={() => {
        setSelectedUserEmail("");
        setCurrentPage(0);
        loadWonItems(0, "");
      }}
    >
      <Column className="w-100 w-lg-100 px-1 pl-sm-3 pl-md-3 pl-lg-3 mt-4">
        <Table
          trackingKey="Itens ganhos - Table"
          model={wonItemsModel}
          data={wonItemsTableData}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          setTableOrder={setTableOrder}
          tableOrder={tableOrder}
          showPagination={true}
          DropdownTemplate={WonItemsDetailDropdown}
          customProps={{ onOpenQuotation: handleOpenQuotation }}
          portalCustomHeader
          className="w-100 lost-won-items-table lost-won-items-table--table won-items-modal-table"
          emptyDataMessage="Nenhum item ganho encontrado"
        />
      </Column>
    </ManagementPanelModalCard>
  );
};
